import React, { useMemo, useState } from 'react';

import { useRecommendation } from './useRecommendation';
import { Card } from './Card';
import { Counter } from '../../../my-appointments/components/CounterBadge';
import ButtonComponent from 'components/button/buttonComponent';
import Loader from 'components/loaderComponent';
import { RecommendationItem } from './RecommendationItem';
import { ArchiveRecommendationModal } from './ArchiveRecommendationModal';
import { useModalParams } from 'components/modal/useModalManager';
import { pageIds, PARAM_MODALS_IDENTIFIERS } from 'utilities/constants';
import { useLocation, useNavigate } from 'react-router-dom';
import { ServiceRequestStatus } from 'graphql/generated/hasura';
import useShopCart from 'hooks/useShopCart';
import { useGetPage } from 'hooks/useGetPage';
import { twMerge } from 'tailwind-merge';
import PaginateBlock from 'components/PaginateBlock';

const initialRecommendationsFilter = {
  statuses: [ServiceRequestStatus.Active],
  limit: 5,
};

interface IRecommendationsProps {
  title?: string;
  subtitle?: React.ReactNode | string;
  showArchiveLink?: boolean;
  defaultExpanded?: boolean;
  maxHeight?: string;
}

export const Recommendations = ({
  title,
  subtitle,
  showArchiveLink = true,
  defaultExpanded = true,
  maxHeight = 'max-h-auto',
}: IRecommendationsProps) => {
  const { data: locale, loading: isLocaleLoading } = useGetPage({
    locale: 'en',
    pageId: pageIds.RECOMMENDATION,
  });
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const offset = queryParams.get('recommended-offset');

  const [filter, setFilter] = useState(
    () =>
      ({
        ...initialRecommendationsFilter,
        offset: offset ? +offset : 0,
      } as Record<string, unknown>),
  );

  const { isOpen: isArchiveModalOpen } = useModalParams(
    PARAM_MODALS_IDENTIFIERS.ARCHIVE_RECOMMENDATIONS_MODAL,
  );

  const {
    data,
    loading: dataLoading,
    total,
    moveToArchive,
    handleDeleteRecommendation,
  } = useRecommendation(filter);

  const setNewOffset = (offset: number) =>
    setFilter((vars) => ({ ...vars, offset }));

  const hasShowPaginate = useMemo(
    () =>
      (total ?? 0) >
      +(filter?.limit || (initialRecommendationsFilter?.limit ?? 0)),
    [total, filter.limit],
  );

  const paginateSubtitle = useMemo(
    () =>
      `Showing ${(filter.offset as number) + 1}-${Math.min(
        (filter.offset as number) + (filter.limit as number),
        total ?? 0,
      )} recommended products from ${total ?? 0}`,
    [total, filter],
  );

  const { addRecommendationToCart } = useShopCart();

  const handleOpenArchiveModal = () => {
    navigate(
      `.?${PARAM_MODALS_IDENTIFIERS.ARCHIVE_RECOMMENDATIONS_MODAL}=true`,
    );
  };

  if (isLocaleLoading) return null;

  if ((!total || !data?.length) && !dataLoading) return null;

  return (
    <>
      {isArchiveModalOpen && (
        <ArchiveRecommendationModal
          locale={locale}
          addRecommendationToCart={addRecommendationToCart}
        />
      )}

      <Card
        testId="recommendation-card"
        title={title || locale?.title}
        rightComponentWrap={true}
        defaultExpanded={defaultExpanded || !!offset}
        rightComponent={
          showArchiveLink ? (
            <ButtonComponent
              testID="archive-button"
              type="underline"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleOpenArchiveModal();
              }}
              containerClassName="text-sm"
            >
              {locale.archive}
            </ButtonComponent>
          ) : null
        }
        subtitle={
          !!data?.length &&
          (subtitle || (
            <div className="text-base font-medium text-med-gray-3 mt-1">
              <div className="inline desktop:block">{locale.subtitle1}</div>
              <div className="inline desktop:block">{locale.subtitle2}</div>
            </div>
          ))
        }
        counter={
          <div>
            <Counter total={total || 0} />
          </div>
        }
        titleClassName="text-xl desktop:text-h3 text-neutral-800  font-semibold"
      >
        {dataLoading ? (
          <div className="my-auto" data-testid="upcoming-appointments-loading">
            <Loader />
          </div>
        ) : (
          <div
            className={twMerge(
              'w-full flex flex-col gap-2.5 overflow-y-auto',
              maxHeight,
            )}
          >
            {data?.length ? (
              data?.map((recommendation) => (
                <RecommendationItem
                  key={recommendation.id}
                  recommendation={recommendation}
                  locale={locale}
                  addRecommendationToCart={addRecommendationToCart}
                  moveToArchive={() => moveToArchive(recommendation.id)}
                  deleteRecommendation={() =>
                    handleDeleteRecommendation(recommendation.id)
                  }
                />
              ))
            ) : (
              <div className="flex h-full items-center justify-center text-lg py-6 font-semibold text-btn-black">
                {locale?.noRecommendations || 'No recommendations yet'}
              </div>
            )}
          </div>
        )}
        {hasShowPaginate && (
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className="flex max-desktop:flex-col w-full gap-4 justify-between items-center"
          >
            <p className="text-sm font-medium text-med-gray-3">
              {paginateSubtitle}
            </p>
            <PaginateBlock
              itemsPerPage={filter.limit as number}
              offsetParams="recommended-offset"
              totalItems={total ?? 0}
              setNewOffset={setNewOffset}
            />
          </div>
        )}
      </Card>
    </>
  );
};
